import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, logout, hasScope } = useAuth();
  const location = useLocation();


  const isDeveloper = hasScope('dev');
  const isAdmin = hasScope('admin');

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: '📊', shortName: 'Home' },
    { name: 'Profiles', href: '/profiles', icon: '👤', shortName: 'Profiles' },
    { name: 'Transactions', href: '/transactions', icon: '💳', shortName: 'History' },
    { name: 'QR Payment', href: '/qr-payment', icon: '📱', shortName: 'QR Pay' },
    ...(isDeveloper ? [
      { name: 'WebSocket Monitor', href: '/websocket-monitor', icon: '🔌', shortName: 'Monitor' },
      { name: 'Mobile Clients', href: '/mobile-clients', icon: '📲', shortName: 'Devices' },
      { name: 'Bank Transactions', href: '/dev/bank-transactions', icon: '🏦', shortName: 'BankTx' }
    ] : []),
    { name: 'Settings', href: '/settings', icon: '⚙️', shortName: 'Settings' },
  ];

  const isCurrentPage = (href: string) => {
    return location.pathname === href;
  };

  const handleLogout = () => {
    logout();
  };

  const getPageTitle = () => {
    const currentPage = navigation.find(nav => nav.href === location.pathname);
    return currentPage ? currentPage.name : 'QRPay';
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-16 sm:pb-0">
      {/* Desktop Navigation */}
      <nav className="hidden sm:block bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <Link to="/dashboard" className="text-xl font-bold text-blue-600">
                  QRPay
                </Link>
              </div>

              {/* Desktop Navigation */}
              <div className="ml-6 flex space-x-8">
                {navigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium ${
                      isCurrentPage(item.href)
                        ? 'border-blue-500 text-gray-900'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }`}
                  >
                    <span className="mr-2">{item.icon}</span>
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* User Menu */}
            <div className="ml-6 flex items-center">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  Welcome, {user?.fullName}
                </span>
                <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                  {isDeveloper ? 'Developer' : isAdmin ? 'Admin' : 'User'}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-gray-500 hover:text-gray-700 text-sm font-medium"
                >
                  Sign out
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Header */}
      <header className="sm:hidden bg-white shadow-sm border-b border-gray-200 fixed top-0 left-0 right-0 z-40">
        <div className="flex items-center justify-between h-14 px-4">
          <h1 className="text-lg font-semibold text-gray-900">{getPageTitle()}</h1>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              {isDeveloper ? 'Dev' : isAdmin ? 'Admin' : 'User'}
            </span>
            <button
              onClick={handleLogout}
              className="text-gray-500 hover:text-gray-700 text-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-14 sm:pt-0">
        {children}
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="grid grid-cols-5 h-16">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col items-center justify-center space-y-1 ${
                isCurrentPage(item.href)
                  ? 'text-blue-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="text-lg">{item.icon}</span>
              <span className="text-xs font-medium">{item.shortName}</span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Footer - Hidden on mobile */}
      <footer className="hidden sm:block bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              © 2024 QRPay. All rights reserved.
            </div>
            <div className="flex space-x-4 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-700">Privacy</a>
              <a href="#" className="hover:text-gray-700">Terms</a>
              <a href="#" className="hover:text-gray-700">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
